import type { FieldSpec } from '@moment/horizon-data-access/platform'
import type { SecurityFieldName } from '@moment/sorm-client-data-access/schema'
import type { QuoteSideKey } from './security-types'

import { createLogger } from '@/lib/logger'

import {
  bookFields,
  evalFields,
  SECURITY_ENTITY,
  SECURITY_ID_FIELD,
} from './security-wire-fields'

// Post-tax YTW, tax-equivalent yield and effective tax rate as per-request
// exprdsl computed columns over Security, ported from horizon's
// liveinventory reqparse/tax_params.go. Keep the branch order in lock-step
// with it.

const logger = createLogger('shared/sorm/security/security-tax-expressions')

/** The investor's tax inputs, as the proposal sends them to live inventory. */
export interface SecurityTaxInputs {
  tax_profile?: string
  federal_tax_rate?: number
  state_tax_rate?: number
  residing_state?: string
  custom_income_tax?: number
  custom_capital_gains_tax?: number
}

/** The Security field serving each per-bond input of the tax math. */
export const TAX_INPUT_FIELDS = {
  type: 'asset_class',
  ticker: 'bond_ticker',
  isin: SECURITY_ID_FIELD,
  municipalState: 'municipal_state',
  // Never the coerced non-raw columns: they read false where the vendor reports
  // nothing, which sends corporates down the federal-exempt branch.
  municipalTaxableFederal: 'municipal_taxable_federal_raw',
  isStateTaxApplicable: 'is_state_tax_applicable_raw',
  couponType: 'coupon_type',
  maturityDate: 'maturity_date',
  currency: 'currency',
  iceSector1: 'ice_sector_1',
  qcbIndicator: 'qcb_indicator',
  coefA1: 'mid_tax_a1',
  coefA2: 'mid_tax_a2',
  coefB1: 'mid_tax_b1',
  coefB2: 'mid_tax_b2',
  coefC11: 'mid_tax_c11',
} as const satisfies Record<string, SecurityFieldName>

type TaxInput = keyof typeof TAX_INPUT_FIELDS

type BinaryOperator = '+' | '-' | '*' | '/' | '=' | '<=' | 'AND' | 'OR'

/** An exprdsl expression; `renderTaxExpr` spells it. */
export type TaxExpr =
  | { kind: 'num'; value: number }
  | { kind: 'int'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'bool'; value: boolean }
  | { kind: 'field'; field: string }
  | { kind: 'binary'; op: BinaryOperator; left: TaxExpr; right: TaxExpr }
  | { kind: 'in'; value: TaxExpr; options: ReadonlyArray<string> }
  | { kind: 'isNull'; value: TaxExpr }
  | {
      kind: 'case'
      whens: ReadonlyArray<readonly [TaxExpr, TaxExpr]>
      otherwise: TaxExpr
    }
  | {
      kind: 'call'
      name: 'POW' | 'COALESCE' | 'SUBSTR' | 'DATE_DIFF' | 'TODAY'
      args: ReadonlyArray<TaxExpr>
    }

const num = (value: number): TaxExpr => ({ kind: 'num', value })
const int = (value: number): TaxExpr => ({ kind: 'int', value })
const str = (value: string): TaxExpr => ({ kind: 'str', value })
const bool = (value: boolean): TaxExpr => ({ kind: 'bool', value })
const input = (name: TaxInput): TaxExpr => ({
  kind: 'field',
  field: TAX_INPUT_FIELDS[name],
})
const binary =
  (op: BinaryOperator) =>
  (left: TaxExpr, right: TaxExpr): TaxExpr => ({
    kind: 'binary',
    op,
    left,
    right,
  })
const add = binary('+')
const sub = binary('-')
const mul = binary('*')
const div = binary('/')
const eq = binary('=')
const lte = binary('<=')
const and = (...parts: TaxExpr[]): TaxExpr => parts.reduce(binary('AND'))
const or = binary('OR')
const isIn = (value: TaxExpr, options: ReadonlyArray<string>): TaxExpr => ({
  kind: 'in',
  value,
  options,
})
const isNull = (value: TaxExpr): TaxExpr => ({ kind: 'isNull', value })
const caseWhen = (
  whens: ReadonlyArray<readonly [TaxExpr, TaxExpr]>,
  otherwise: TaxExpr,
): TaxExpr => ({ kind: 'case', whens, otherwise })
const call = (
  name: Extract<TaxExpr, { kind: 'call' }>['name'],
  ...args: TaxExpr[]
): TaxExpr => ({ kind: 'call', name, args })

const US_TERRITORIES = ['PR', 'GU', 'VI', 'AS', 'MP']
const US_STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]
const STATE_EXEMPT_AGENCIES = ['FHLB', 'FFCB', 'TVA', 'FICO', 'REFCORP']
const CROSS_DEDUCTIBLE_STATES = ['AL', 'IA', 'MO', 'MT', 'OR']
const NORTH_DAKOTA = 'ND'
const ANNUAL_COMPOUNDING_ISIN_PREFIXES = ['DE', 'FR', 'NL', 'IT']

type TaxProfile = 'us_taxable' | 'custom_taxable' | 'uk_taxable' | 'non_taxable'

interface TaxParams {
  profile: TaxProfile
  federal: number
  state: number
  residingState: string
  income: number
  capitalGains: number
}

function taxProfile(value: string | undefined): TaxProfile {
  switch (value) {
    case 'custom_taxable':
    case 'uk_taxable':
    case 'non_taxable':
      return value
    default:
      return 'us_taxable'
  }
}

// Live inventory's defaults: 37% federal, no state, us_taxable.
function taxParams(tax: SecurityTaxInputs): TaxParams {
  const state = (tax.residing_state ?? '').trim().toUpperCase()
  return {
    profile: taxProfile(tax.tax_profile),
    federal: tax.federal_tax_rate ?? 0.37,
    state: tax.state_tax_rate ?? 0,
    residingState:
      US_STATE_CODES.includes(state) || US_TERRITORIES.includes(state)
        ? state
        : '',
    income: tax.custom_income_tax ?? 0,
    capitalGains: tax.custom_capital_gains_tax ?? 0,
  }
}

function stateBenefit(tax: TaxParams): TaxExpr {
  if (tax.residingState === NORTH_DAKOTA) return bool(true)
  return or(
    eq(input('municipalState'), str(tax.residingState)),
    isIn(input('municipalState'), US_TERRITORIES),
  )
}

function stateExemptAgency(): TaxExpr {
  return and(
    eq(input('type'), str('agency')),
    isIn(input('ticker'), STATE_EXEMPT_AGENCIES),
  )
}

const isTreasury = () => eq(input('type'), str('treasury'))
const taxableFederal = (value: boolean) =>
  eq(input('municipalTaxableFederal'), bool(value))
const stateTaxApplicable = (value: boolean) =>
  eq(input('isStateTaxApplicable'), bool(value))

function effectiveTaxRate(tax: TaxParams): TaxExpr {
  const both = num(tax.federal + tax.state)
  return caseWhen(
    [
      [isTreasury(), num(tax.federal)],
      [stateExemptAgency(), num(tax.federal)],
      [and(taxableFederal(true), stateTaxApplicable(true)), both],
      [
        and(taxableFederal(true), stateTaxApplicable(false), stateBenefit(tax)),
        num(tax.federal),
      ],
      [
        and(
          taxableFederal(false),
          stateTaxApplicable(false),
          stateBenefit(tax),
        ),
        num(0),
      ],
      [taxableFederal(false), num(tax.state)],
    ],
    both,
  )
}

function ukCapitalGainsExempt(): TaxExpr {
  return or(ukGiltStrip(), call('COALESCE', input('qcbIndicator'), bool(false)))
}

function ukGiltStrip(): TaxExpr {
  return and(
    eq(input('iceSector1'), str('Sovereign')),
    eq(input('currency'), str('GBP')),
  )
}

function postTaxPolynomial(
  ytw: TaxExpr,
  income: number,
  capitalGains: number,
): TaxExpr {
  const s = num(income)
  const g = num(capitalGains)
  return [
    mul(input('coefA1'), s),
    mul(input('coefB1'), g),
    mul(mul(mul(num(0.5), input('coefA2')), s), s),
    mul(mul(mul(num(0.5), input('coefB2')), g), g),
    mul(mul(input('coefC11'), s), g),
  ].reduce(add, ytw)
}

function compoundingFrequency(): TaxExpr {
  return caseWhen(
    [
      [
        or(
          eq(input('currency'), str('EUR')),
          isIn(
            call('SUBSTR', input('isin'), int(1), int(2)),
            ANNUAL_COMPOUNDING_ISIN_PREFIXES,
          ),
        ),
        num(1),
      ],
    ],
    num(2),
  )
}

function zeroYearsToMaturity(): TaxExpr {
  return div(
    call('DATE_DIFF', str('day'), call('TODAY'), input('maturityDate')),
    num(365.25),
  )
}

function zeroCouponAnnualMarkToMarket(
  ytw: TaxExpr,
  taxRate: TaxExpr,
  m: TaxExpr,
): TaxExpr {
  const r = div(ytw, num(100))
  const effective = sub(call('POW', add(num(1), div(r, m)), m), num(1))
  const postTax = mul(
    m,
    sub(
      call(
        'POW',
        add(num(1), mul(effective, sub(num(1), taxRate))),
        div(num(1), m),
      ),
      num(1),
    ),
  )
  return caseWhen([[lte(ytw, num(0)), ytw]], mul(postTax, num(100)))
}

function zeroCouponDeferred(
  ytw: TaxExpr,
  taxRate: TaxExpr,
  m: TaxExpr,
  n: TaxExpr,
): TaxExpr {
  const r = div(ytw, num(100))
  const mn = mul(m, n)
  const gross = call('POW', add(num(1), div(r, m)), mn)
  const postTax = mul(
    m,
    sub(
      call(
        'POW',
        add(mul(gross, sub(num(1), taxRate)), taxRate),
        div(num(1), mn),
      ),
      num(1),
    ),
  )
  return caseWhen(
    [[or(or(lte(ytw, num(0)), isNull(n)), lte(n, num(0))), ytw]],
    mul(postTax, num(100)),
  )
}

const isZeroCoupon = () => eq(input('couponType'), str('zero'))

function postTaxYtw(ytw: TaxExpr, tax: TaxParams): TaxExpr {
  switch (tax.profile) {
    case 'custom_taxable':
      return caseWhen(
        [
          [
            isZeroCoupon(),
            zeroCouponDeferred(
              ytw,
              num(tax.income),
              compoundingFrequency(),
              zeroYearsToMaturity(),
            ),
          ],
        ],
        postTaxPolynomial(ytw, tax.income, tax.capitalGains),
      )
    case 'uk_taxable': {
      const polynomial = caseWhen(
        [[ukCapitalGainsExempt(), postTaxPolynomial(ytw, tax.income, 0)]],
        postTaxPolynomial(ytw, tax.income, tax.capitalGains),
      )
      const zero = caseWhen(
        [
          [
            ukGiltStrip(),
            zeroCouponAnnualMarkToMarket(
              ytw,
              num(tax.income),
              compoundingFrequency(),
            ),
          ],
        ],
        zeroCouponDeferred(
          ytw,
          num(tax.income),
          compoundingFrequency(),
          zeroYearsToMaturity(),
        ),
      )
      return caseWhen([[isZeroCoupon(), zero]], polynomial)
    }
    case 'non_taxable':
      return ytw
    case 'us_taxable': {
      const rate = effectiveTaxRate(tax)
      return caseWhen(
        [[isZeroCoupon(), zeroCouponAnnualMarkToMarket(ytw, rate, num(2))]],
        mul(ytw, sub(num(1), rate)),
      )
    }
  }
}

function bothExemptRate(tax: TaxParams): TaxExpr {
  const federal = num(tax.federal)
  const state = num(tax.state)
  if (CROSS_DEDUCTIBLE_STATES.includes(tax.residingState)) {
    const stateEffective = mul(sub(num(1), federal), state)
    return add(mul(sub(num(1), stateEffective), federal), stateEffective)
  }
  return add(mul(sub(num(1), federal), state), federal)
}

function taxEquivalentYield(ytw: TaxExpr, tax: TaxParams): TaxExpr {
  switch (tax.profile) {
    case 'custom_taxable':
    case 'non_taxable':
      return ytw
    case 'uk_taxable':
      return caseWhen(
        [
          [
            ukCapitalGainsExempt(),
            div(postTaxYtw(ytw, tax), sub(num(1), num(tax.income))),
          ],
        ],
        ytw,
      )
    case 'us_taxable': {
      const grossUp = (rate: TaxExpr) => div(ytw, sub(num(1), rate))
      return caseWhen(
        [
          [isTreasury(), grossUp(num(tax.state))],
          [stateExemptAgency(), grossUp(num(tax.state))],
          [and(taxableFederal(true), stateTaxApplicable(true)), ytw],
          [
            and(
              taxableFederal(true),
              stateTaxApplicable(false),
              stateBenefit(tax),
            ),
            grossUp(num(tax.state)),
          ],
          [
            and(
              taxableFederal(false),
              stateTaxApplicable(false),
              stateBenefit(tax),
            ),
            grossUp(bothExemptRate(tax)),
          ],
          [taxableFederal(false), grossUp(num(tax.federal))],
        ],
        ytw,
      )
    }
  }
}

function numberLiteral(value: number): string {
  if (!Number.isFinite(value)) {
    throw new RangeError(`Tax rate ${value} is not a finite number`)
  }
  if (Number.isInteger(value)) return value.toFixed(1)
  const text = String(value)
  return text.includes('e') ? value.toFixed(20).replace(/0+$/, '') : text
}

/** Spells an expression in exprdsl, every field as a `Security.` reference. */
function renderTaxExpr(expr: TaxExpr): string {
  switch (expr.kind) {
    case 'num':
      return numberLiteral(expr.value)
    case 'int':
      return String(expr.value)
    case 'str':
      return `'${expr.value.replaceAll("'", "''")}'`
    case 'bool':
      return expr.value ? 'TRUE' : 'FALSE'
    case 'field':
      return `${SECURITY_ENTITY}.${expr.field}`
    case 'binary':
      return `(${renderTaxExpr(expr.left)} ${expr.op} ${renderTaxExpr(expr.right)})`
    case 'in':
      return `(${renderTaxExpr(expr.value)} IN (${expr.options
        .map((option) => renderTaxExpr(str(option)))
        .join(', ')}))`
    case 'isNull':
      return `(${renderTaxExpr(expr.value)} IS NULL)`
    case 'case':
      return `(CASE ${expr.whens
        .map(
          ([when, then]) =>
            `WHEN ${renderTaxExpr(when)} THEN ${renderTaxExpr(then)}`,
        )
        .join(' ')} ELSE ${renderTaxExpr(expr.otherwise)} END)`
    case 'call':
      return `${expr.name}(${expr.args.map(renderTaxExpr).join(', ')})`
  }
}

/** Every Security field an expression reads. */
function taxExprFields(expr: TaxExpr): Set<string> {
  const fields = new Set<string>()
  const visit = (node: TaxExpr): void => {
    switch (node.kind) {
      case 'field':
        fields.add(node.field)
        return
      case 'binary':
        visit(node.left)
        visit(node.right)
        return
      case 'in':
      case 'isNull':
        visit(node.value)
        return
      case 'case':
        for (const [when, then] of node.whens) {
          visit(when)
          visit(then)
        }
        visit(node.otherwise)
        return
      case 'call':
        node.args.forEach(visit)
        return
      default:
        return
    }
  }
  visit(expr)
  return fields
}

type TaxYieldKind = 'postTaxYtw' | 'taxEquivalentYield'

/** Where a tax-aware yield's pre-tax YTW comes from. */
type QuoteSource = 'best' | 'mpe'

export const EFFECTIVE_TAX_RATE_COLUMN = 'effective_tax_rate'

const TAX_YIELD_SUFFIX: Readonly<Record<TaxYieldKind, string>> = {
  postTaxYtw: 'post_tax_ytw',
  taxEquivalentYield: 'tax_equivalent_yield',
}

/** The computed column alias of one side's tax-aware yield, e.g. `best_ask_post_tax_ytw`. */
export function taxYieldColumn(
  source: QuoteSource,
  side: QuoteSideKey,
  kind: TaxYieldKind,
): string {
  return `${source}_${side}_${TAX_YIELD_SUFFIX[kind]}`
}

type TaxColumnBuilder = (tax: TaxParams) => TaxExpr

function taxYieldColumns(
  side: QuoteSideKey,
  source: QuoteSource,
): Array<[string, TaxColumnBuilder]> {
  const ytw: TaxExpr = {
    kind: 'field',
    field: source === 'best' ? bookFields(side).ytw : evalFields(side).ytw,
  }
  return [
    [taxYieldColumn(source, side, 'postTaxYtw'), (tax) => postTaxYtw(ytw, tax)],
    [
      taxYieldColumn(source, side, 'taxEquivalentYield'),
      (tax) => taxEquivalentYield(ytw, tax),
    ],
  ]
}

const TAX_COLUMNS = new Map<string, TaxColumnBuilder>([
  [EFFECTIVE_TAX_RATE_COLUMN, effectiveTaxRate],
  ...taxYieldColumns('ask', 'best'),
  ...taxYieldColumns('ask', 'mpe'),
  ...taxYieldColumns('bid', 'best'),
  ...taxYieldColumns('bid', 'mpe'),
])

/** Whether a field name is a tax computed column rather than a Security field. */
export function isTaxColumn(name: string): boolean {
  return TAX_COLUMNS.has(name)
}

/** A tax column's expression for one investor; undefined for any other name. */
export function taxColumnExpr(
  name: string,
  tax: SecurityTaxInputs,
): TaxExpr | undefined {
  return TAX_COLUMNS.get(name)?.(taxParams(tax))
}

const warnedColumns = new Set<string>()

/**
 * A tax column rendered for one investor, or null when the deployed schema
 * lacks a field it reads. A missing input makes the column unavailable rather
 * than computing it from a stand-in, warned once per column and missing set.
 */
export function renderTaxColumn(
  name: string,
  tax: SecurityTaxInputs,
  isFieldAvailable: (field: string) => boolean,
): string | null {
  const expr = taxColumnExpr(name, tax)
  if (!expr) return null
  const missing = [...taxExprFields(expr)].filter(
    (field) => !isFieldAvailable(field),
  )
  if (missing.length > 0) {
    const key = `${name}:${missing.join(',')}`
    if (!warnedColumns.has(key)) {
      warnedColumns.add(key)
      logger.warn('Security does not serve the inputs of a tax column', {
        column: name,
        missing,
      })
    }
    return null
  }
  return renderTaxExpr(expr)
}

/** Computed columns for the requested tax column names the schema can serve. */
export function taxColumnFieldSpecs(
  names: Iterable<string>,
  tax: SecurityTaxInputs,
  isFieldAvailable: (field: string) => boolean,
): FieldSpec[] {
  const specs: FieldSpec[] = []
  for (const name of new Set(names)) {
    const expr = renderTaxColumn(name, tax, isFieldAvailable)
    if (expr !== null) specs.push({ alias: name, expr })
  }
  return specs
}
